/**
 * MemberAccount (FHIR: Account)
 *
 * Extracted model encapsulating all Account-related logic for member-practice relationships.
 *
 * Responsibilities:
 * - Provide ergonomic getters and helpers for common Account fields
 * - Implement creation flows (migrations, stripe), activation/inactivation
 * - Manage service identifiers and service choice extensions
 * - Offer search helpers and subscription support
 *
 * Notes:
 * - Preserves existing behavior from Practice and Member wrappers
 * - Uses explicit query mapping to FHIR search parameters
 * - Avoids Promise.all for unbounded arrays
 *
 * Size: this file is approaching the 1000-LOC refactor-signal threshold (see root AGENTS.md).
 * Purchase-related query logic already lives in `./purchase-data.ts`, extracted for the same
 * reason — when adding a new cohesive section, prefer pulling free functions (medplum + a
 * reference in, no `this`) into a sibling `*-data.ts` file over growing this class further.
 */

import { type MedplumClient, OperationOutcomeError, resolveId } from "@medplum/core";
import type { Account, Resource } from "@medplum/fhirtypes";
import {
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourceRef, ValueOf } from "@vibrant/models/fhir/fhir-resource-types";
import { cleanObject } from "@vibrant/models/fhir/fhir-resource-utils";
import { paginatedSearch } from "@/lib/fhir/bundle";
import { createLogger } from "@/lib/logger";
import {
  type NullableExtension,
  pruneSystemValuePairs,
  toCodeableConcept,
  toFhirExtensions,
} from "@/lib/medplum/utils";
import { getExcludeQuarantinedTagQuery } from "@/lib/migration/quarantine";
import { time } from "@/lib/time";
import { chunkArray } from "@/lib/utils/chunk-array";
import {
  addFiredWorkflowStep,
  type FiredWorkflowStep,
  parseFiredWorkflowSteps,
  serializeFiredWorkflowSteps,
} from "@/lib/workflows/fired-steps";
import {
  clearWorkflowStepBlocker,
  findWorkflowStepBlocker,
  parseWorkflowStepBlockers,
  serializeWorkflowStepBlockers,
  upsertWorkflowStepBlocker,
  type WorkflowStepBlocker,
} from "@/lib/workflows/step-blockers";
import { VIBRANT_IDENTIFIER_SYSTEM_SERVICES } from "@/models/practice/services/constants";
import { createObjectValueGuard, findExtension } from "@/models/utils";
import type { Purchase } from "./Purchase";
import {
  searchHintChargesForAccount,
  searchHintMembershipsForAccount,
  searchPurchasesForAccount,
} from "./purchase-data";

const logger = createLogger("MemberAccount");

/** Member ids per search. More ids can push a joined query past the request-line limit. */
const MAX_MEMBER_IDS_PER_QUERY = 100;

/** The onboarding workflow a member was assigned at invite, plus its version at that moment. */
export interface OnboardingWorkflowAssignment {
  /** `PlanDefinition/<id>`. */
  reference: string;
  /** Workflow title snapshotted at assign time, so a reader can name it without a second fetch. */
  display?: string;
  /** `PracticeWorkflow.version` when the assignment was written. */
  version: number;
}

export interface MemberAccountCreateRequest {
  member: ResourceRef;
  practice: ResourceRef;
  status: Account["status"];
  serviceChoiceIds?: string[];
  serviceId?: string;
  syncedFromDataMigration?: boolean;
  syncedFromStripe?: boolean;
  inviteWithoutPayment?: boolean;
  inviteStatus?: InviteStatus;
  /** Snapshot of Survey ids manually assigned at invite time (no-payment path only). */
  requiredOnboardingSurveyIds?: string[];
}

export interface MemberAccountUpdateRequest {
  member: ResourceRef;
  practice: ResourceRef;
  status?: Account["status"];
  serviceChoiceIds?: string[] | null;
  serviceId?: string | null;
  syncedFromDataMigration?: boolean;
  syncedFromStripe?: boolean;
  inviteWithoutPayment?: boolean;
  inviteStatus?: InviteStatus;
  /**
   * Snapshot of Survey ids required for this member's onboarding, resolved once (at payment for
   * the with-payment path, at invite time for the no-payment path) and immune to later edits of a
   * Service's attached questionnaires. `null` clears the snapshot.
   */
  requiredOnboardingSurveyIds?: string[] | null;
  /** ISO timestamp set once the "onboarding complete" email has been sent. */
  onboardingCompleteNotifiedAt?: string;
  /** Who deactivated the account. `null` clears it, which is what reactivation does. */
  deactivationSource?: DeactivationSource | null;
  /** Onboarding workflow assigned at invite. Written once; nothing reassigns it. */
  onboardingWorkflow?: OnboardingWorkflowAssignment;
  /** The full skip set, replacing whatever is stored. `null` clears it. */
  skippedWorkflowStepIds?: string[] | null;
  /**
   * The full blocker set, replacing whatever is stored. `null` clears it.
   *
   * Serialized JSON rather than codings, because a blocker carries a vendor message and a
   * timestamp as well as a step id — see `lib/workflows/step-blockers.ts`.
   */
  workflowStepBlockers?: string | null;
  /** The full fired-automated-steps set, replacing whatever is stored. `null` clears it. */
  firedWorkflowSteps?: string | null;
}

export interface MemberAccountSearchQuery {
  member?: ResourceRef;
  practice?: ResourceRef;
  status?: Account["status"];
  /**
   * Omit Accounts still quarantined by a data migration. Migration imports create Accounts with
   * `status: active` already set, so status alone does not distinguish an approved member from one
   * whose dataset is still pending review (and may yet be rejected and deleted).
   */
  excludeQuarantined?: boolean;
}

export type MemberAccountStatus = ValueOf<typeof MemberAccount.Status>;
export type InviteStatus = ValueOf<typeof MemberAccount.InviteStatus>;
export type DeactivationSource = ValueOf<typeof MemberAccount.DeactivationSource>;

/** Thrown when a write would reopen an Account that a chart removal closed. Permanent. */
export class RemovedChartAccountError extends Error {
  constructor() {
    super(MemberAccount.Error.RemovedChartAccount);
    this.name = "RemovedChartAccountError";
  }
}

export class MemberAccount extends FhirResource<Account> {
  static readonly ResourceType = "Account" as const;

  static readonly Identifier = {
    Services: VIBRANT_IDENTIFIER_SYSTEM_SERVICES,
  } as const;

  static readonly Coding = {
    InviteStatus: "vibrant.account.inviteStatus",
  } as const;

  static readonly Status = {
    Active: "active",
    Inactive: "inactive",
  } as const;

  /**
   * Why an account is inactive. `markInactive` requires one, because the two cases need different
   * treatment on the next Hint sync and are otherwise indistinguishable after the fact.
   */
  static readonly DeactivationSource = {
    /** A human took this member out of service in Ultralight. Hint must not undo it. */
    Provider: "provider",
    /** Hint reported the patient or their membership as ended. Hint may reverse it. */
    Hint: "hint",
    /**
     * The account was active when its chart was removed (tombstoned), and `Member.remove` closed
     * it. `Member.restore` reopens it. No sync reopens it.
     */
    Removal: "removal",
    /**
     * The account was already inactive with the `hint` marker when its chart was removed.
     * `Member.remove` stamps this marker so that no sync reopens it. `Member.restore` keeps it
     * inactive and puts the `hint` marker back.
     */
    RemovedWhileInactive: "removed-while-inactive",
    /**
     * The account was already inactive with no marker when its chart was removed. `Member.restore`
     * keeps it inactive and clears the marker again. The Hint sync never reactivates an unmarked
     * account, so it must come back unmarked.
     */
    RemovedWhileUnmarked: "removed-while-unmarked",
  } as const;

  static readonly InviteStatus = {
    Pending: "pending",
    Invited: "invited",
    InviteAccepted: "invite-accepted",
  } as const;

  static readonly Extension = {
    SyncedFromDataMigration: "vibrant.account.syncedFromDataMigration",
    SyncedFromStripe: "vibrant.account.syncedFromStripe",
    ServiceChoices: "vibrant.services.choices",
    InviteWithoutPayment: "vibrant.invite.withoutPayment",
    RequiredOnboardingSurveys: "vibrant.account.requiredOnboardingSurveys",
    OnboardingCompleteNotifiedAt: "vibrant.account.onboardingCompleteNotifiedAt",
    OnboardingWorkflow: "vibrant.account.onboardingWorkflow",
    /**
     * Workflow steps a provider marked "skip for this patient".
     *
     * A skip is the one piece of per-step progress that *is* recorded rather than derived. Every
     * other step reads its completion off clinical data — a paid `ChargeItem`, a booked
     * `Appointment` — and there is no datum that means "the practice decided this one does not
     * apply to them". So it is written here, on the same `Account` that carries the assignment.
     */
    SkippedWorkflowSteps: "vibrant.account.skippedWorkflowSteps",
    /**
     * Who took this account inactive. Written by every path that deactivates, read by the Hint
     * membership sync before it reactivates — a provider's deliberate deactivation must survive
     * the next routine sync from Hint (ENG-5273).
     */
    DeactivationSource: "vibrant.account.deactivationSource",
    /**
     * Why this patient's workflow steps cannot complete, when something concrete is stopping them.
     *
     * On the `Account` for the same reason the skip set is: it is per patient **per practice**, and
     * there is no other resource that means "this patient's run of this workflow". A lab rejection
     * lives on the order, but "the patient's provider cannot authorize orders" has no order to
     * live on, and both must surface in the same place on the chart.
     */
    WorkflowStepBlockers: "vibrant.account.workflowStepBlockers",
    /**
     * Which automated practice-owned steps (`send-message`, `send-chat`, `care-team-task`) have
     * already run for this patient's workflow.
     *
     * Here for the same reason the skip set and the blockers are: it is per patient per practice,
     * and nothing else records "the welcome message for this run already went out". Step completion
     * is otherwise derived live, so without this record the executor would re-fire on every
     * recompute. See `lib/workflows/fired-steps.ts`.
     */
    FiredWorkflowSteps: "vibrant.account.firedWorkflowSteps",
  } as const;

  /** Coding system for the step ids under {@link Extension.SkippedWorkflowSteps}. */
  static readonly SkippedWorkflowStepCodingSystem = "vibrant.account.workflowStepId";

  /**
   * Sub-extension urls under {@link Extension.OnboardingWorkflow}. The two are written together and
   * read together — see {@link onboardingWorkflowAssignment}.
   */
  static readonly OnboardingWorkflowPart = {
    Workflow: "workflow",
    Version: "version",
  } as const;

  // Shared coding system for Questionnaire references embedded in a CodeableConcept extension —
  // same system/code shape as Service.Extension.Surveys and PracticeAppointmentType.Extension.Surveys.
  static readonly SurveyReferenceCodingSystem = "vibrant.survey-reference" as const;

  /**
   * A searchable `meta.tag` mirroring the onboarding-workflow assignment.
   *
   * Medplum cannot search an extension, so without this the automated-step reconcile sweep would
   * have to scan every active Account — which breaks past `_offset=10000` in a large project. The
   * sweep queries `Account?_tag=<system>|has-onboarding-workflow` instead, a bounded set.
   */
  static readonly Tag = {
    OnboardingWorkflow: {
      system: "vibrant.account.tags",
      code: "has-onboarding-workflow",
    },
  } as const;

  /**
   * Keeps the onboarding-workflow {@link Tag} in step with the assignment extension on every write.
   *
   * Runs through the `deriveOnWrite` hook on the resource as it will be stored, the same mechanism
   * `MedicalEncounter` uses for its inbox tags.
   */
  static readonly deriveOnWrite = (resource: Resource): Resource =>
    resource.resourceType === "Account"
      ? MemberAccount.withOnboardingWorkflowTag(resource)
      : resource;

  /** Adds the onboarding-workflow tag when the assignment extension is present, removes it otherwise. */
  static withOnboardingWorkflowTag(account: Account): Account {
    const hasWorkflow = !!findExtension(account, MemberAccount.Extension.OnboardingWorkflow);
    const { system, code } = MemberAccount.Tag.OnboardingWorkflow;
    const otherTags = (account.meta?.tag ?? []).filter(
      (candidate) => !(candidate.system === system && candidate.code === code),
    );
    const tag = hasWorkflow ? [...otherTags, { system, code }] : otherTags;
    return { ...account, meta: { ...account.meta, tag: tag.length > 0 ? tag : undefined } };
  }

  static readonly Error = {
    /**
     * Safety guard to prevent cross-practice patient attachment.
     *
     * If a Patient already has an Account (member-practice relationship) for a different practice,
     * we must not create a new Account for another practice automatically.
     */
    CrossPracticeAssociation:
      "This patient record is already associated with a different practice. Please contact support.",
    MultipleActiveAccounts:
      "This patient record has multiple active accounts for the same practice. Please contact support.",
    /** An upsert tried to set a removal-closed Account active. See `Member.restore`. */
    RemovedChartAccount:
      "This patient's chart was removed, so its account cannot be activated. Restore the chart first.",
  } as const;

  //----------------------------------------------------------------------------------------------
  //                                           GETTERS
  //----------------------------------------------------------------------------------------------

  get status(): MemberAccountStatus {
    const status = this.resource.status;
    if (MemberAccount.isStatus(status)) {
      return status;
    }
    return MemberAccount.Status.Active;
  }

  /**
   * The stored FHIR status, unmapped. `status` folds `entered-in-error`, `on-hold` and `unknown`
   * into active; use this when those must be told apart.
   */
  get fhirStatus(): Account["status"] {
    return this.resource.status;
  }

  get inviteStatus(): InviteStatus {
    const codings = this.resource.type?.coding ?? [];
    const coding = codings.find((c) => c.system === MemberAccount.Coding.InviteStatus);
    const code = coding?.code;
    if (MemberAccount.isInviteStatus(code)) {
      return code;
    }
    return MemberAccount.InviteStatus.Pending;
  }

  get needsInvite(): boolean {
    return this.inviteStatus === MemberAccount.InviteStatus.Pending;
  }

  get wasInvited(): boolean {
    return this.inviteStatus === MemberAccount.InviteStatus.Invited;
  }

  get acceptedInvite(): boolean {
    return this.inviteStatus === MemberAccount.InviteStatus.InviteAccepted;
  }

  get ownerPracticeRef(): ResourceRef | undefined {
    const owner = this.resource.owner;
    if (!owner?.reference) return undefined;
    return {
      reference: owner.reference,
      display: owner.display,
      identifier: owner.identifier,
    };
  }

  get ownerPracticeId(): string | undefined {
    const ref = this.ownerPracticeRef;
    return ref ? (resolveId(ref) ?? undefined) : undefined;
  }

  get memberRef(): ResourceRef | undefined {
    const subject = this.resource.subject?.[0];
    const ref = subject?.reference;
    if (!ref?.startsWith("Patient/")) return undefined;
    return {
      reference: ref,
      display: subject?.display,
      identifier: subject?.identifier,
    };
  }

  get memberId(): string | undefined {
    const ref = this.memberRef;
    return ref ? (resolveId(ref) ?? undefined) : undefined;
  }

  get serviceId(): string | undefined {
    const identifier = this.resource.identifier?.find(
      (identifier) => identifier.system === MemberAccount.Identifier.Services,
    );
    return identifier?.value;
  }

  get isActive(): boolean {
    return this.status === MemberAccount.Status.Active;
  }

  get isInactive(): boolean {
    return this.status === MemberAccount.Status.Inactive;
  }

  /**
   * Who took this account inactive, when that is recorded.
   *
   * `undefined` on an inactive account means it predates the marker (ENG-5273) — treat that as
   * unknown, not as "Hint did it". Reactivating an account whose deactivation nobody recorded is
   * a judgment call for the caller, not something to infer here.
   */
  get deactivationSource(): DeactivationSource | undefined {
    const value = findExtension(
      this.resource,
      MemberAccount.Extension.DeactivationSource,
    )?.valueCode;
    return createObjectValueGuard(MemberAccount.DeactivationSource)(value) ? value : undefined;
  }

  /** True when a human deactivated this member. */
  get isProviderDeactivated(): boolean {
    return this.deactivationSource === MemberAccount.DeactivationSource.Provider;
  }

  /** True when a chart removal closed or stamped this account - see `Member.remove`. */
  get isClosedByRemoval(): boolean {
    return (
      this.deactivationSource === MemberAccount.DeactivationSource.Removal ||
      this.deactivationSource === MemberAccount.DeactivationSource.RemovedWhileInactive ||
      this.deactivationSource === MemberAccount.DeactivationSource.RemovedWhileUnmarked
    );
  }

  /**
   * True when an automated sync (Hint, repair migrations) must leave this inactive account alone:
   * a provider closed it, or a chart removal did.
   */
  get isSyncProtected(): boolean {
    return this.isProviderDeactivated || this.isClosedByRemoval;
  }

  get wasInvitedWithoutPayment(): boolean {
    const ext = findExtension(this.resource, MemberAccount.Extension.InviteWithoutPayment);
    return ext?.valueBoolean === true;
  }

  get syncedFromDataMigration(): boolean {
    const ext = findExtension(this.resource, MemberAccount.Extension.SyncedFromDataMigration);
    return ext?.valueBoolean === true;
  }

  get syncedFromStripe(): boolean {
    const ext = findExtension(this.resource, MemberAccount.Extension.SyncedFromStripe);
    return ext?.valueBoolean === true;
  }

  /**
   * Snapshot of Survey ids required for this member's onboarding. Empty until resolution has
   * run — see {@link snapshotRequiredOnboardingSurveys} and
   * `data/member/onboarding-questionnaires.ts`.
   */
  get requiredOnboardingSurveyIds(): string[] {
    const ext = findExtension(this.resource, MemberAccount.Extension.RequiredOnboardingSurveys);
    const codings = ext?.valueCodeableConcept?.coding ?? [];

    return codings
      .filter(
        (coding) =>
          coding.system === MemberAccount.SurveyReferenceCodingSystem &&
          coding.code?.startsWith("Questionnaire/"),
      )
      .map((coding) => coding.code!.replace("Questionnaire/", ""));
  }

  /**
   * Timestamp the "onboarding complete" email was sent, if ever. Idempotency guard for
   * {@link markOnboardingCompleteNotified} — see `data/member/notify-onboarding-complete.ts`.
   */
  get onboardingCompleteNotifiedAt(): string | undefined {
    const ext = findExtension(this.resource, MemberAccount.Extension.OnboardingCompleteNotifiedAt);
    return ext?.valueDateTime;
  }

  /**
   * The onboarding workflow this member was put on at invite, and the workflow's version at that
   * moment. Absent for every member invited before assignment existed, and for the creation paths
   * that never invite (chart-only, embed booker, calendar booking, EHR migration) — those keep the
   * practice-wide fallback in `lib/hooks/use-practice-onboarding-workflow.ts`.
   *
   * The patient walks the workflow's *current* definition, not the version recorded here. An
   * assignment is a pointer plus this patient's progress, so a provider's later edit reaches
   * someone still in flight — including a fix to a step that cannot run. The version is what makes
   * "finished" durable once per-step completion is recorded: an assignment completed at version N
   * stays complete when a later edit bumps the workflow past it. See
   * `docs/workflows/patient-onboarding.md`.
   */
  get onboardingWorkflowAssignment(): OnboardingWorkflowAssignment | undefined {
    const ext = findExtension(this.resource, MemberAccount.Extension.OnboardingWorkflow);
    const parts = ext?.extension ?? [];
    const workflow = parts.find(
      (part) => part.url === MemberAccount.OnboardingWorkflowPart.Workflow,
    )?.valueReference;
    const version = parts.find(
      (part) => part.url === MemberAccount.OnboardingWorkflowPart.Version,
    )?.valueInteger;
    const reference = workflow?.reference;

    // Both halves or nothing. A half-written assignment reads as unassigned and falls back to the
    // practice-wide rule, rather than pointing a patient at a workflow we cannot name a version for.
    if (!reference?.startsWith("PlanDefinition/")) return undefined;
    if (typeof version !== "number" || !Number.isInteger(version)) return undefined;

    return { reference, display: workflow?.display, version };
  }

  //----------------------------------------------------------------------------------------------
  //                                      SERVICE HELPERS
  //----------------------------------------------------------------------------------------------

  /**
   * Service ids proposed on the Account extension only (no Purchase lookup) — the synchronous
   * subset of {@link getProposedServiceIds}, for callers that already have purchases in hand (or
   * don't need them) and only want the extension-derived signal without an async round trip.
   */
  get proposedServiceIdsFromExtension(): string[] {
    const ext = findExtension(this.resource, MemberAccount.Extension.ServiceChoices);
    const codings = ext?.valueCodeableConcept?.coding ?? [];
    return codings
      .filter((coding) => coding.system === MemberAccount.Identifier.Services)
      .map((coding) => coding.code)
      .filter((code): code is string => Boolean(code));
  }

  /**
   * Returns the union of services referenced by this account’s extension and any linked purchases.
   * Optionally accept `prefetchedPurchases` to reuse an existing purchase query and avoid duplicate
   * network requests.
   */
  async getProposedServiceIds(
    medplum: MedplumClient,
    options?: { prefetchedPurchases?: Purchase[] },
  ): Promise<string[]> {
    const extensionServiceIds = this.proposedServiceIdsFromExtension;
    const providedPurchases = options?.prefetchedPurchases;
    if (providedPurchases) {
      const purchaseServiceIds = providedPurchases
        .map((purchase) => purchase.serviceId)
        .filter((id): id is string => Boolean(id));

      const uniqueIds = new Set<string>([...extensionServiceIds, ...purchaseServiceIds]);
      return Array.from(uniqueIds);
    }

    try {
      const purchases = await searchPurchasesForAccount(medplum, this.referenceObject);
      const purchaseServiceIds = purchases
        .map((purchase) => purchase.serviceId)
        .filter((id): id is string => Boolean(id));

      const uniqueIds = new Set<string>([...extensionServiceIds, ...purchaseServiceIds]);
      return Array.from(uniqueIds);
    } catch (error) {
      logger.warn("getProposedServiceIds: error loading purchases", { error });
      // Fallback to extension-only service IDs
      return Array.from(new Set(extensionServiceIds));
    }
  }

  /**
   * Gets Hint charges (purchases) associated with this account with pagination.
   * Filters for purchases that have Hint charge item coding.
   */
  async getHintCharges(
    medplum: MedplumClient,
    options: { limit: number; offset: number },
  ): Promise<{
    charges: Purchase[];
    totalCount: number;
  }> {
    return searchHintChargesForAccount(medplum, this.referenceObject, options);
  }

  /**
   * Gets Hint memberships (purchases) associated with this account.
   * Can return multiple memberships per patient.
   * Optionally filters by active status.
   */
  async getHintMemberships(
    medplum: MedplumClient,
    options?: { limit?: number; offset?: number },
  ): Promise<{
    memberships: Purchase[];
    totalCount: number;
  }> {
    return searchHintMembershipsForAccount(medplum, this.referenceObject, options);
  }

  /**
   * Gets active Hint memberships (purchases) associated with this account.
   * Filters memberships where status is "active".
   */
  async getActiveHintMemberships(medplum: MedplumClient): Promise<Purchase[]> {
    const { memberships } = await searchHintMembershipsForAccount(medplum, this.referenceObject);
    return memberships.filter((m) => m.hintMembershipStatus === "active");
  }

  //----------------------------------------------------------------------------------------------
  //                                      STATIC OPERATIONS
  //----------------------------------------------------------------------------------------------

  /**
   * Upserts a member account uniquely identified by (memberId, practiceId).
   * Ensures we never create duplicate accounts for the same tuple.
   */
  static async upsert(
    medplum: MedplumClient,
    data: MemberAccountCreateRequest | MemberAccountUpdateRequest,
  ): Promise<MemberAccount> {
    const existing = await MemberAccount.searchOne(medplum, {
      member: data.member,
      practice: data.practice,
    });

    if (existing) {
      MemberAccount.assertNotReopeningRemovedChart(existing, data);
      const patchObject = this.toFhirUpdateObject(data);
      const updated = await MemberAccount.patchResource(medplum, {
        id: existing.id,
        patch: patchObject,
      });
      return new MemberAccount(updated);
    }

    // Create new account
    if (data.status === undefined) {
      throw new Error("upsert: 'status' is required when creating a new account");
    }

    // Prevent cross-practice attachment: if this Patient already has an Account for a *different*
    // practice, we must not create a new Account automatically.
    //
    // Note: We intentionally check *all* accounts for this member (not just "one") so we can:
    // - correctly detect cross-practice attachment
    // - treat same-practice accounts as an idempotent/race condition and patch them instead
    const accountsForMember = await MemberAccount.search(
      medplum,
      { member: data.member },
      { limit: 10 },
    );

    const targetPracticeRef = data.practice?.reference;
    const targetPracticeId = resolveId(data.practice);

    const isSamePractice = (account: MemberAccount): boolean => {
      const ownerPracticeId = account.ownerPracticeId;
      const ownerPracticeRef = account.ownerPracticeRef?.reference;

      // If we can't prove it's the same practice, treat as unsafe.
      if (!ownerPracticeId && !ownerPracticeRef) return false;

      // Prefer ID comparison when possible.
      if (targetPracticeId && ownerPracticeId) {
        return ownerPracticeId === targetPracticeId;
      }

      // Fallback to reference-string comparison (still deterministic).
      if (targetPracticeRef && ownerPracticeRef) {
        return ownerPracticeRef === targetPracticeRef;
      }

      return false;
    };

    const conflictingAccount = accountsForMember.find((account) => !isSamePractice(account));
    if (conflictingAccount) {
      logger.error("upsert: blocked cross-practice Account creation", {
        targetPracticeRef,
        targetPracticeId,
        memberRef: data.member?.reference,
        memberId: resolveId(data.member),
        existingAccountId: conflictingAccount.id,
        existingPracticeId: conflictingAccount.ownerPracticeId,
      });
      throw new Error(MemberAccount.Error.CrossPracticeAssociation);
    }

    // If we got here and we have an account for this member, it must belong to the same practice.
    // This can happen under concurrency (another request created the account between our initial
    // (member, practice) lookup and the create attempt). Treat as an idempotent upsert.
    const samePracticeExisting = accountsForMember.find(
      (account) => account.id && isSamePractice(account),
    );
    if (samePracticeExisting?.id) {
      MemberAccount.assertNotReopeningRemovedChart(samePracticeExisting, data);
      logger.info(
        "upsert: found existing Account for same practice; patching instead of creating",
        {
          accountId: samePracticeExisting.id,
          practiceId: samePracticeExisting.ownerPracticeId,
          memberId: resolveId(data.member),
        },
      );

      const patchObject = this.toFhirUpdateObject(data);
      const updated = await MemberAccount.patchResource(medplum, {
        id: samePracticeExisting.id,
        patch: patchObject,
      });
      return new MemberAccount(updated);
    }

    const shouldDefaultInviteStatus = data.inviteStatus === undefined;
    const payload = shouldDefaultInviteStatus
      ? { ...data, inviteStatus: MemberAccount.InviteStatus.Pending }
      : data;

    const createRequest: MemberAccountCreateRequest = {
      ...(payload as MemberAccountCreateRequest),
      status: data.status,
    };
    const created = await MemberAccount.createResource(
      medplum,
      this.toFhirCreateObject(createRequest),
    );
    return new MemberAccount(created);
  }

  /**
   * Creates the member's active Account for a practice unless one already exists, in one
   * conditional create keyed on (subject, owner). A retry therefore finishes a half-done write
   * rather than duplicating it.
   *
   * For callers whose AccessPolicy grants Account `create` and `search` only (the API serving
   * identity): no read, no patch, and no `practiceOrganization`, because that policy stamps the
   * compartment. It skips `upsert`'s cross-practice guard, so the caller must already know the
   * member belongs to this practice.
   */
  static async createForPracticeIfNoneExist(
    medplum: MedplumClient,
    { member, practice }: { member: ResourceRef; practice: ResourceRef },
  ): Promise<{ account: MemberAccount; wasCreated: boolean }> {
    const criteria = new URLSearchParams({
      subject: member.reference ?? "",
      owner: practice.reference ?? "",
    });
    const result = await MemberAccount.executeBatch(medplum, {
      resourceType: "Bundle",
      type: "transaction",
      entry: [
        {
          resource: {
            resourceType: "Account",
            ...this.toFhirCreateObject({
              member,
              practice,
              status: MemberAccount.Status.Active,
              inviteStatus: MemberAccount.InviteStatus.Pending,
            }),
          } as Account,
          request: { method: "POST", url: "Account", ifNoneExist: criteria.toString() },
        },
      ],
    });
    const entry = result.entry?.[0];
    if (entry?.resource?.resourceType === "OperationOutcome") {
      throw new OperationOutcomeError(entry.resource);
    }
    if (entry?.resource?.resourceType !== "Account") {
      throw new Error("Practice account transaction returned no Account");
    }
    const status = entry.response?.status?.split(" ")[0];
    if (status !== "200" && status !== "201") {
      throw new Error("Practice account transaction returned an unexpected status");
    }
    return { account: new MemberAccount(entry.resource), wasCreated: status === "201" };
  }

  /**
   * Refuses an upsert that would set a removal-closed Account active. Only `Member.restore` may
   * reopen it (ENG-5780). Throws instead of a silent skip: a caller such as a paid Stripe checkout
   * must fail where the job reports it, not look like success.
   */
  private static assertNotReopeningRemovedChart(
    existing: MemberAccount,
    data: MemberAccountCreateRequest | MemberAccountUpdateRequest,
  ): void {
    if (data.status !== MemberAccount.Status.Active || !existing.isInactive) return;
    if (!existing.isClosedByRemoval) return;
    logger.warn("upsert: refused to activate an account closed by a chart removal", {
      memberId: existing.memberId ?? null,
      practiceId: existing.ownerPracticeId ?? null,
      accountId: existing.id,
      deactivationSource: existing.deactivationSource ?? null,
      reason: "chart-removed",
    });
    throw new RemovedChartAccountError();
  }

  /**
   * Updates a known Account by id when the caller has already resolved the intended record.
   */
  static async update(
    medplum: MedplumClient,
    id: string,
    data: MemberAccountUpdateRequest,
  ): Promise<MemberAccount> {
    const patchObject = this.toFhirUpdateObject(data);
    const updated = await MemberAccount.patchResource(medplum, { id, patch: patchObject });
    return new MemberAccount(updated);
  }

  /**
   * Adds a service for the member and upserts the account to active.
   */
  static async addService(
    medplum: MedplumClient,
    data: {
      member: ResourceRef;
      practice: ResourceRef;
      serviceId: string;
      inviteStatus?: InviteStatus;
    },
  ): Promise<MemberAccount> {
    const existingActiveAccount = await MemberAccount.searchActiveForMemberPractice(medplum, {
      member: data.member,
      practice: data.practice,
    });

    if (existingActiveAccount) {
      return MemberAccount.update(medplum, existingActiveAccount.id, {
        member: data.member,
        practice: data.practice,
        status: MemberAccount.Status.Active,
        serviceId: data.serviceId,
        inviteWithoutPayment: false,
        inviteStatus: data.inviteStatus,
      });
    }

    const upserted = await MemberAccount.upsert(medplum, {
      member: data.member,
      practice: data.practice,
      status: "active",
      serviceId: data.serviceId,
      inviteWithoutPayment: false,
      inviteStatus: data.inviteStatus,
    });
    return upserted;
  }

  //----------------------------------------------------------------------------------------------
  //                                         READ HELPERS
  //----------------------------------------------------------------------------------------------

  static async getById(medplum: MedplumClient, id: string): Promise<MemberAccount | undefined> {
    const accountResource = await MemberAccount.searchOneResource(medplum, { _id: id });
    return accountResource ? new MemberAccount(accountResource) : undefined;
  }

  //----------------------------------------------------------------------------------------------
  //                                         SEARCH HELPERS
  //----------------------------------------------------------------------------------------------

  static async searchOne(
    medplum: MedplumClient,
    query: MemberAccountSearchQuery,
  ): Promise<MemberAccount | undefined> {
    const accountResource = await MemberAccount.searchOneResource(medplum, this.toFhirQuery(query));
    return accountResource ? new MemberAccount(accountResource) : undefined;
  }

  /**
   * Finds the active account for a member/practice pair and blocks ambiguous duplicate state.
   */
  static async searchActiveForMemberPractice(
    medplum: MedplumClient,
    query: Pick<MemberAccountSearchQuery, "member" | "practice">,
  ): Promise<MemberAccount | undefined> {
    const accounts = await MemberAccount.search(
      medplum,
      {
        member: query.member,
        practice: query.practice,
        status: MemberAccount.Status.Active,
      },
      { limit: 10 },
    );

    if (accounts.length <= 1) {
      return accounts[0];
    }

    logger.error("searchActiveForMemberPractice: multiple active Accounts found", {
      memberRef: query.member?.reference,
      memberId: resolveId(query.member),
      practiceRef: query.practice?.reference,
      practiceId: resolveId(query.practice),
      accountIds: accounts.map((account) => account.id),
    });
    throw new Error(MemberAccount.Error.MultipleActiveAccounts);
  }

  /**
   * Searches accounts for a given query.
   */
  static async search(
    medplum: MedplumClient,
    query: MemberAccountSearchQuery,
    options?: SearchOptions,
  ): Promise<MemberAccount[]> {
    const accounts = await MemberAccount.searchResources(medplum, this.toFhirQuery(query), options);
    return accounts.map((accountResource) => new MemberAccount(accountResource));
  }

  /**
   * Active accounts for several members in one practice, in a single search (not one per member).
   * `subject` takes a comma-joined ref list, which FHIR reads as OR. The caller is an appointment's
   * attendees, so the list is small and needs no chunking.
   */
  static async searchActiveByMembers(
    medplum: MedplumClient,
    { memberIds, practiceId }: { memberIds: string[]; practiceId: string },
  ): Promise<MemberAccount[]> {
    if (memberIds.length === 0) return [];
    const accounts = await MemberAccount.searchResources(
      medplum,
      {
        subject: memberIds.map((id) => `Patient/${id}`).join(","),
        owner: `Organization/${practiceId}`,
        status: "active",
      },
      { limit: memberIds.length },
    );
    return accounts.map((account) => new MemberAccount(account));
  }

  //----------------------------------------------------------------------------------------------
  //                                    PRACTICE LINKAGE HELPERS
  //----------------------------------------------------------------------------------------------

  /**
   * Checks whether a member has at least one Account linked to the given practice via Account.owner.
   * This is the authoritative practice-linkage check — prefer this over Patient.managingOrganization,
   * which is not reliably populated for all patients (see ENG-3466).
   */
  static async isMemberLinkedToPractice(
    medplum: MedplumClient,
    {
      memberId,
      practiceId,
    }: {
      memberId: string;
      practiceId: string;
    },
  ): Promise<boolean> {
    const accounts = await MemberAccount.search(medplum, {
      member: { reference: `Patient/${memberId}` },
      practice: { reference: `Organization/${practiceId}` },
    });
    return accounts.length > 0;
  }

  /**
   * Returns the members of this list that have an Account in the practice, in one paged search per
   * 100 members. The batch form of `isMemberLinkedToPractice`. It reads every page, because a
   * member can hold more than one Account in the practice.
   */
  static async filterMemberIdsLinkedToPractice(
    medplum: MedplumClient,
    { memberIds, practiceId }: { memberIds: string[]; practiceId: string },
  ): Promise<Set<string>> {
    const practiceQuery = MemberAccount.toFhirQuery({
      practice: { reference: `Organization/${practiceId}` },
    });
    const batches = await Promise.all(
      chunkArray(memberIds, MAX_MEMBER_IDS_PER_QUERY).map(async (chunk) => {
        const query = cleanObject({
          ...practiceQuery,
          subject: chunk.map((memberId) => `Patient/${memberId}`).join(","),
          _count: 1000,
        });
        const ids: string[] = [];
        for await (const account of paginatedSearch<Account>({
          medplum,
          resourceType: MemberAccount.ResourceType,
          query,
        })) {
          const { memberId } = new MemberAccount(account);
          if (memberId) ids.push(memberId);
        }
        return ids;
      }),
    );
    return new Set(batches.flat());
  }

  /**
   * Asserts that a member belongs to the given practice via Account.owner.
   * Throws if no Account links the member to the practice.
   */
  static async assertMemberBelongsToPractice(
    medplum: MedplumClient,
    {
      memberId,
      practiceId,
    }: {
      memberId: string;
      practiceId: string;
    },
  ): Promise<void> {
    const isLinked = await MemberAccount.isMemberLinkedToPractice(medplum, {
      memberId,
      practiceId,
    });
    if (!isLinked) {
      throw new Error("Member does not belong to this practice");
    }
  }

  /**
   * Resolves the one practice that owns this member, from `Account.owner`.
   *
   * Counts distinct owner practices, not Accounts. A member legitimately holds several Accounts
   * for one practice: `upsert` blocks only *cross-practice* creation and patches a same-practice
   * duplicate instead, and migration `025-deduplicate-member-accounts` skips any practice with
   * more than 1000 Accounts. So an Account count above one proves nothing about ambiguity —
   * `lib/cal/member.ts` and `lib/auth/authorization.ts` both read the practice this way.
   *
   * `practiceId` is set only when exactly one practice owns the member. The counts come back so
   * a caller can log which case it hit: no Account at all, no `owner` on the Account, or two
   * practices that genuinely disagree.
   */
  static async resolveSoleOwnerPracticeId(
    medplum: MedplumClient,
    { memberId }: { memberId: string },
  ): Promise<{ practiceId?: string; accountCount: number; practiceCount: number }> {
    const accounts = await MemberAccount.search(medplum, {
      member: { reference: `Patient/${memberId}` },
    });

    const practiceIds = Array.from(
      new Set(
        accounts
          .map((account) => account.ownerPracticeId?.trim())
          .filter((practiceId): practiceId is string => !!practiceId?.length),
      ),
    );

    return {
      practiceId: practiceIds.length === 1 ? practiceIds[0] : undefined,
      accountCount: accounts.length,
      practiceCount: practiceIds.length,
    };
  }

  //----------------------------------------------------------------------------------------------
  //                                         SUBSCRIPTIONS
  //----------------------------------------------------------------------------------------------

  static subscribe(
    medplum: MedplumClient,
    query: MemberAccountSearchQuery,
    callback: SubscriptionCallback<MemberAccount>,
  ) {
    return this.subscribeToResource(medplum, this.toFhirQuery(query), (resource, metadata) =>
      callback(new MemberAccount(resource), metadata),
    );
  }

  //----------------------------------------------------------------------------------------------
  //                                   STATUS HELPERS
  //----------------------------------------------------------------------------------------------

  /**
   * Activates an existing inactive account for the member within the practice.
   */
  async markActive(medplum: MedplumClient): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("markActive: member or practice not found");
    }

    // Clear the deactivation marker: it describes why the account is *inactive*, and a stale
    // "provider" marker on a live account would block the next Hint reactivation for no reason.
    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      status: MemberAccount.Status.Active,
      deactivationSource: null,
    });
  }

  /**
   * Marks an existing active account as inactive, recording who did it.
   *
   * `source` is required rather than defaulted. Hint's membership sync reactivates accounts it
   * took down and must not touch the ones a provider took down deliberately, and before ENG-5273
   * the two wrote identical records — so an un-marked deactivation is exactly the bug this
   * parameter exists to prevent.
   */
  async markInactive(
    medplum: MedplumClient,
    { source }: { source: DeactivationSource },
  ): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("markInactive: member or practice not found");
    }

    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      status: MemberAccount.Status.Inactive,
      deactivationSource: source,
    });
  }

  //----------------------------------------------------------------------------------------------
  //                                   INVITE HELPERS
  //----------------------------------------------------------------------------------------------

  /**
   * Sets invite status to "invited". No-op if already invited or accepted. Does not change Account.status.
   */
  async markInvited(medplum: MedplumClient): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("markInvited: member or practice not found");
    }
    if (this.wasInvited || this.acceptedInvite) {
      return this;
    }
    const updated = await MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      inviteStatus: MemberAccount.InviteStatus.Invited,
    });
    return updated;
  }

  /**
   * Sets invite status to "invite-accepted". No-op if already accepted. Does not change Account.status.
   */
  async markInviteAccepted(medplum: MedplumClient): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("markInviteAccepted: member or practice not found");
    }
    if (this.acceptedInvite) {
      return this;
    }
    const updated = await MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      inviteStatus: MemberAccount.InviteStatus.InviteAccepted,
    });
    return updated;
  }

  //----------------------------------------------------------------------------------------------
  //                                  ONBOARDING QUESTIONNAIRE HELPERS
  //----------------------------------------------------------------------------------------------

  /**
   * Writes the resolved required-onboarding-survey-id snapshot. Idempotent — writing the same
   * list twice is a no-op-equivalent. Pass `[]` to clear.
   */
  async snapshotRequiredOnboardingSurveys(
    medplum: MedplumClient,
    surveyIds: string[],
  ): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("snapshotRequiredOnboardingSurveys: member or practice not found");
    }

    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      requiredOnboardingSurveyIds: surveyIds.length > 0 ? surveyIds : null,
    });
  }

  /**
   * Records which onboarding workflow this member was put on, and its version at that moment.
   *
   * A no-op once an assignment exists. Assignment happens at invite and nothing reassigns it — a
   * second write would swap a patient's checklist mid-flow. Like every `FhirResource` write this
   * returns a new instance, so a caller that keeps using its own reference must reassign it.
   */
  async assignOnboardingWorkflow(
    medplum: MedplumClient,
    assignment: OnboardingWorkflowAssignment,
  ): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("assignOnboardingWorkflow: member or practice not found");
    }

    if (this.onboardingWorkflowAssignment) {
      return this;
    }

    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      onboardingWorkflow: assignment,
    });
  }

  /**
   * Workflow step ids a provider has skipped for this patient.
   *
   * Read alongside the derived completion signals — see `lib/workflows/patient-signals.ts`. A
   * skipped step counts as done everywhere: the patient's checklist, the portal gate, and the
   * clinic-facing status. Skipping a step that still gated the patient would be a control that
   * looks like it worked and does nothing.
   */
  get skippedWorkflowStepIds(): string[] {
    const ext = findExtension(this.resource, MemberAccount.Extension.SkippedWorkflowSteps);
    return (ext?.valueCodeableConcept?.coding ?? [])
      .map((coding) => coding.code)
      .filter((code): code is string => !!code);
  }

  /**
   * Why this patient's steps cannot complete, as recorded by whatever tried and failed.
   *
   * Diagnostic only — a blocker never changes what counts as done. The step stays outstanding
   * either way; this says whether anyone should expect it to move on its own.
   */
  get workflowStepBlockers(): readonly WorkflowStepBlocker[] {
    const ext = findExtension(this.resource, MemberAccount.Extension.WorkflowStepBlockers);
    return parseWorkflowStepBlockers(ext?.valueString);
  }

  /** The automated steps that have already fired for this patient's workflow. */
  get firedWorkflowSteps(): readonly FiredWorkflowStep[] {
    const ext = findExtension(this.resource, MemberAccount.Extension.FiredWorkflowSteps);
    return parseFiredWorkflowSteps(ext?.valueString);
  }

  /**
   * Records or clears the reason one step cannot complete.
   *
   * Pass `blocker: undefined` when the step goes through, so a fixed problem stops being reported.
   * Idempotent: re-recording an identical blocker, or clearing one that was never there, returns
   * `this` without a write.
   */
  async setWorkflowStepBlocker(
    medplum: MedplumClient,
    { stepId, blocker }: { stepId: string; blocker: WorkflowStepBlocker | undefined },
  ): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("setWorkflowStepBlocker: member or practice not found");
    }

    const current = this.workflowStepBlockers;
    const existing = findWorkflowStepBlocker(current, stepId);

    if (!blocker && !existing) return this;
    // `recordedAt` is deliberately excluded from the comparison: a repeat of the same failure is
    // the same blocker, and rewriting it on every page load would churn the Account's version.
    if (blocker && existing?.code === blocker.code && existing.detail === blocker.detail) {
      return this;
    }

    const next = blocker
      ? upsertWorkflowStepBlocker(current, blocker)
      : clearWorkflowStepBlocker(current, stepId);

    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      workflowStepBlockers: next.length > 0 ? serializeWorkflowStepBlockers(next) : null,
    });
  }

  /**
   * Records that an automated step has fired, so a later recompute does not fire it again.
   *
   * Idempotent: a step already in the fired set returns `this` without a write. The caller writes
   * this in the same step that performed the send/create and re-reads it fresh, so a redelivery
   * finds the mark and skips — the executor's primary idempotency guard.
   */
  async markWorkflowStepFired(
    medplum: MedplumClient,
    { stepId, firedAt }: { stepId: string; firedAt: string },
  ): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("markWorkflowStepFired: member or practice not found");
    }

    const current = this.firedWorkflowSteps;
    if (current.some((entry) => entry.stepId === stepId)) return this;

    const next = addFiredWorkflowStep(current, { stepId, firedAt });

    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      firedWorkflowSteps: serializeFiredWorkflowSteps(next),
    });
  }

  /**
   * Adds or removes a step from this patient's skip set.
   *
   * Idempotent in both directions: skipping an already-skipped step and unskipping one that was
   * never skipped both return `this` without a write, so a double-click cannot produce two
   * versions of the `Account`.
   */
  async setWorkflowStepSkipped(
    medplum: MedplumClient,
    { stepId, isSkipped }: { stepId: string; isSkipped: boolean },
  ): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("setWorkflowStepSkipped: member or practice not found");
    }

    const current = this.skippedWorkflowStepIds;
    if (current.includes(stepId) === isSkipped) {
      return this;
    }

    const next = isSkipped
      ? [...current, stepId]
      : current.filter((candidate) => candidate !== stepId);

    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      skippedWorkflowStepIds: next,
    });
  }

  /**
   * Records that the "onboarding complete" email was sent. No-op if already recorded — the
   * idempotency guard consumed by `data/member/notify-onboarding-complete.ts` to ensure the
   * email fires exactly once per member.
   */
  async markOnboardingCompleteNotified(medplum: MedplumClient): Promise<MemberAccount> {
    const memberRef = this.memberRef;
    const practiceRef = this.ownerPracticeRef;

    if (!memberRef || !practiceRef) {
      throw new Error("markOnboardingCompleteNotified: member or practice not found");
    }
    if (this.onboardingCompleteNotifiedAt) {
      return this;
    }

    return MemberAccount.update(medplum, this.id, {
      member: memberRef,
      practice: practiceRef,
      onboardingCompleteNotifiedAt: time().toISOString(),
    });
  }

  //----------------------------------------------------------------------------------------------
  //                                      TYPES & MAPPERS
  //----------------------------------------------------------------------------------------------
  private static toFhirCreateObject(data: MemberAccountCreateRequest): ResourceData<Account> {
    const { member, practice, status } = data;

    const extensions = this.buildExtensionsFromData(data);
    const identifiers = this.buildIdentifiersFromData(data);
    const type = this.buildTypeFromData(data);

    return {
      subject: [member],
      owner: practice,
      status,
      identifier: identifiers,
      extension: toFhirExtensions(extensions),
      type,
    };
  }

  private static toFhirUpdateObject(
    data: MemberAccountUpdateRequest,
  ): Partial<ResourceData<Account>> {
    const { member, practice, status } = data;

    const extensions = this.buildExtensionsFromData(data);
    const identifiers = this.buildIdentifiersFromData(data);
    const type = this.buildTypeFromData(data);

    return {
      subject: [member],
      owner: practice,
      status,
      identifier: identifiers,
      extension: toFhirExtensions(extensions),
      type,
    };
  }

  /**
   * Builds the list of extensions for an Account from provided flags and selections.
   */
  private static buildExtensionsFromData(data: {
    serviceChoiceIds?: string[] | null;
    syncedFromDataMigration?: boolean;
    syncedFromStripe?: boolean;
    inviteWithoutPayment?: boolean;
    requiredOnboardingSurveyIds?: string[] | null;
    onboardingCompleteNotifiedAt?: string;
    onboardingWorkflow?: OnboardingWorkflowAssignment;
    skippedWorkflowStepIds?: string[] | null;
    workflowStepBlockers?: string | null;
    firedWorkflowSteps?: string | null;
    deactivationSource?: DeactivationSource | null;
  }): NullableExtension[] {
    const {
      serviceChoiceIds,
      syncedFromDataMigration,
      syncedFromStripe,
      inviteWithoutPayment,
      requiredOnboardingSurveyIds,
      onboardingCompleteNotifiedAt,
      onboardingWorkflow,
      skippedWorkflowStepIds,
      workflowStepBlockers,
      firedWorkflowSteps,
      deactivationSource,
    } = data;

    const extensions: NullableExtension[] = [];

    if (serviceChoiceIds === null) {
      extensions.push({
        url: MemberAccount.Extension.ServiceChoices,
        valueCodeableConcept: null,
      });
    } else if (serviceChoiceIds && serviceChoiceIds.length > 0) {
      extensions.push({
        url: MemberAccount.Extension.ServiceChoices,
        valueCodeableConcept: {
          coding: serviceChoiceIds.map((serviceId) => ({
            system: MemberAccount.Identifier.Services,
            code: serviceId,
          })),
        },
      });
    }

    if (requiredOnboardingSurveyIds === null) {
      extensions.push({
        url: MemberAccount.Extension.RequiredOnboardingSurveys,
        valueCodeableConcept: null,
      });
    } else if (requiredOnboardingSurveyIds && requiredOnboardingSurveyIds.length > 0) {
      extensions.push({
        url: MemberAccount.Extension.RequiredOnboardingSurveys,
        valueCodeableConcept: {
          coding: requiredOnboardingSurveyIds.map((surveyId) => ({
            system: MemberAccount.SurveyReferenceCodingSystem,
            code: `Questionnaire/${surveyId}`,
          })),
        },
      });
    }

    if (onboardingWorkflow) {
      extensions.push({
        url: MemberAccount.Extension.OnboardingWorkflow,
        extension: [
          {
            url: MemberAccount.OnboardingWorkflowPart.Workflow,
            valueReference: {
              reference: onboardingWorkflow.reference,
              display: onboardingWorkflow.display,
            },
          },
          {
            url: MemberAccount.OnboardingWorkflowPart.Version,
            valueInteger: onboardingWorkflow.version,
          },
        ],
      });
    }

    if (skippedWorkflowStepIds === null || skippedWorkflowStepIds?.length === 0) {
      // Unskipping the last step clears the extension rather than storing an empty list.
      extensions.push({
        url: MemberAccount.Extension.SkippedWorkflowSteps,
        valueCodeableConcept: null,
      });
    } else if (skippedWorkflowStepIds) {
      extensions.push({
        url: MemberAccount.Extension.SkippedWorkflowSteps,
        valueCodeableConcept: {
          coding: skippedWorkflowStepIds.map((stepId) => ({
            system: MemberAccount.SkippedWorkflowStepCodingSystem,
            code: stepId,
          })),
        },
      });
    }

    if (workflowStepBlockers === null) {
      // Clearing the last blocker removes the extension rather than storing an empty list.
      extensions.push({ url: MemberAccount.Extension.WorkflowStepBlockers, valueString: null });
    } else if (workflowStepBlockers) {
      extensions.push({
        url: MemberAccount.Extension.WorkflowStepBlockers,
        valueString: workflowStepBlockers,
      });
    }

    if (firedWorkflowSteps === null) {
      extensions.push({ url: MemberAccount.Extension.FiredWorkflowSteps, valueString: null });
    } else if (firedWorkflowSteps) {
      extensions.push({
        url: MemberAccount.Extension.FiredWorkflowSteps,
        valueString: firedWorkflowSteps,
      });
    }

    if (deactivationSource === null) {
      extensions.push({ url: MemberAccount.Extension.DeactivationSource, valueCode: null });
    } else if (deactivationSource) {
      extensions.push({
        url: MemberAccount.Extension.DeactivationSource,
        valueCode: deactivationSource,
      });
    }

    if (syncedFromDataMigration !== undefined) {
      extensions.push({
        url: MemberAccount.Extension.SyncedFromDataMigration,
        valueBoolean: syncedFromDataMigration,
      });
    }

    if (syncedFromStripe !== undefined) {
      extensions.push({
        url: MemberAccount.Extension.SyncedFromStripe,
        valueBoolean: syncedFromStripe,
      });
    }

    if (inviteWithoutPayment !== undefined) {
      extensions.push({
        url: MemberAccount.Extension.InviteWithoutPayment,
        valueBoolean: inviteWithoutPayment,
      });
    }

    if (onboardingCompleteNotifiedAt !== undefined) {
      extensions.push({
        url: MemberAccount.Extension.OnboardingCompleteNotifiedAt,
        valueDateTime: onboardingCompleteNotifiedAt,
      });
    }

    return extensions;
  }

  /**
   * Builds identifiers for the Account based on the selected service.
   */
  private static buildIdentifiersFromData(data: { serviceId?: string | null }) {
    const { serviceId } = data;
    return pruneSystemValuePairs([{ system: MemberAccount.Identifier.Services, value: serviceId }]);
  }

  /**
   * Builds the Account.type CodeableConcept merging inviteStatus coding by system, preserving other codings.
   */
  private static buildTypeFromData(data: {
    inviteStatus?: InviteStatus;
  }): Account["type"] | undefined {
    return toCodeableConcept([
      {
        system: MemberAccount.Coding.InviteStatus,
        code: data.inviteStatus,
      },
    ])?.[0];
  }

  static toFhirQuery(query: MemberAccountSearchQuery) {
    const status = query.status;
    const isValidFhirStatus =
      typeof status === "string" &&
      (MemberAccount.isStatus(status) ||
        status === "on-hold" ||
        status === "entered-in-error" ||
        status === "unknown");

    return {
      subject: query.member?.reference,
      owner: query.practice?.reference,
      status: isValidFhirStatus ? status : undefined,
      "_tag:not": query.excludeQuarantined ? getExcludeQuarantinedTagQuery() : undefined,
    };
  }

  private static isStatus(value: string | undefined): value is MemberAccountStatus {
    return createObjectValueGuard(MemberAccount.Status)(value);
  }

  private static isInviteStatus(value: string | undefined): value is InviteStatus {
    return createObjectValueGuard(MemberAccount.InviteStatus)(value);
  }
}
